import type { RouteRecordRaw } from "vue-router";

export const tRoutes: RouteRecordRaw[] = [
  {
    path: "/t/school_classes",
    name: "teacher-school-class-index",
    component: () => import("./school_classes/SchoolClassListPage.vue"),
    meta: {
      requiresAuth: true,
      requiresTeacher: true,
      title: "tools_meta.school_class_prefix",
    },
  },
  {
    path: "/t/school_classes/info",
    name: "teacher-school-class-info",
    component: () => import("./school_classes/info/SchoolClassInfoPage.vue"),
  },
  {
    path: "/t/school_classes/new",
    name: "teacher-school-class-new",
    component: () => import("./school_classes/new/SchoolClassNewPage.vue"),
    meta: {
      requiresAuth: true,
      requiresTeacher: true,
    },
  },
  // Parent layout route for :id - children get schoolClass prop via RouterView
  {
    path: "/t/school_classes/:id",
    name: "teacher-school-class-show",
    component: () => import("./school_classes/SchoolClassLayout.vue"),
    // Navigating by *name* to this record matches the parent only — the ""
    // child is a separate record, so the layout's RouterView renders nothing.
    // The whole app (tabs, project actions, invite overlay, …) navigates to
    // SHOW as a shorthand for "the classroom projects page", so without this
    // the layout had to bounce every one of them on to the "" child with a
    // second `router.replace` — two navigations per click, the page torn down
    // and rebuilt, every request on it fired twice (#12603). Resolving the
    // redirect here keeps it to a single navigation, and makes a click on the
    // already-active tab a duplicate-navigation no-op instead of a remount.
    // Children are unaffected: a redirect only applies when its record is the
    // last matched one, which it never is for /access-codes, /members, etc.
    redirect: (to) => ({
      name: "teacher-school-class-show-projects",
      params: { id: (to.params as { id: string }).id },
    }),
    meta: {
      requiresAuth: true,
      requiresTeacher: true,
    },
    children: [
      {
        path: "",
        name: "teacher-school-class-show-projects",
        component: () =>
          import("./school_classes/[id]/SchoolClassDetailPage.vue"),
        meta: {
          requiresAuth: true,
          requiresTeacher: true,
          title: "tools_meta.school_class_prefix",
        },
      },
      {
        path: "access-codes",
        name: "teacher-school-class-show-access-codes",
        component: () =>
          import("./school_classes/[id]/access-codes/SchoolClassAccessCodesPage.vue"),
        meta: {
          requiresAuth: true,
          requiresTeacher: true,
        },
      },
      {
        // Legacy per-status page. Project status is now chosen via the
        // filter-chips on the main projects page; redirect old bookmarks there.
        path: "archived",
        name: "teacher-school-class-archived",
        redirect: (to) => ({
          name: "teacher-school-class-show",
          params: { id: (to.params as { id: string }).id },
        }),
      },
      {
        // Legacy per-status page (same as archived). Kept as a redirect so old
        // /paused bookmarks land on the consolidated projects page.
        path: "paused",
        redirect: (to) => ({
          name: "teacher-school-class-show",
          params: { id: (to.params as { id: string }).id },
        }),
      },
      {
        path: "archived/projects/:projectId",
        name: "teacher-school-class-show-projects-project-show-archived",
        component: () =>
          import("./school_classes/[id]/archived/projects.[projectId]/SchoolClassArchivedProjectPage.vue"),
        meta: {
          requiresAuth: true,
          requiresTeacher: true,
          title: "tools_meta.school_class_prefix",
        },
      },
      {
        path: "members",
        name: "teacher-school-class-members",
        component: () =>
          import("./school_classes/[id]/members/SchoolClassMemberListPage.vue"),
        meta: {
          requiresAuth: true,
          requiresTeacher: true,
        },
      },
      {
        path: "projects/:projectId",
        name: "teacher-school-class-show-projects-project-show",
        component: () =>
          import("./school_classes/[id]/projects.[projectId]/SchoolClassProjectDetailPage.vue"),
        meta: {
          requiresAuth: true,
          requiresTeacher: true,
          title: "tools_meta.school_class_prefix",
        },
      },
      {
        path: "projects/:projectId/activities/:activityId",
        name: "teacher-school-class-project-activity-show",
        component: () =>
          import("./school_classes/[id]/projects.[projectId]/activities/SchoolClassProjectActivityPage.vue"),
        meta: {
          requiresAuth: true,
          requiresTeacher: true,
          title: "tools_meta.teaching_board_prefix",
        },
      },
      {
        path: "projects/:projectId/share",
        name: "teacher-school-class-show-projects-project-show-share",
        component: () =>
          import("./school_classes/[id]/projects.[projectId]/index/share.vue"),
        meta: {
          requiresAuth: true,
          requiresTeacher: true,
        },
      },
    ],
  },
  // Dot-notation routes that are NOT under the [id] layout
  {
    path: "/t/school_classes/:id/projects/new",
    name: "teacher-school-class-project-new",
    component: () => import("./school_classes/[id].projects.new.vue"),
    meta: {
      requiresAuth: true,
      requiresTeacher: true,
    },
  },
  {
    path: "/t/school_classes/:id/pupils/print",
    name: "teacher-school-class-pupil-print",
    component: () => import("./school_classes/[id].pupils.print.vue"),
    meta: {
      requiresAuth: true,
      requiresTeacher: true,
    },
  },
  {
    path: "/t/projects/:id/edit",
    name: "teacher-project-edit",
    component: () => import("./projects/[id]/edit.vue"),
    meta: {
      requiresAuth: true,
      requiresTeacher: true,
    },
  },
];
